// Cart price revalidation — catches a wholesale discount that went stale
// while the cart was sitting.
//
// `_unit_price` / `_unit_discount` are computed once, at add-to-cart time,
// from the customer's wholesale % AT THAT MOMENT (see fetchWholesaleDiscount
// in expivi-hoodsly.js), and extensions/expivi-dynamic-price +
// expivi-wholesale-discount just trust those properties forever after —
// neither ever recomputes anything. A cart lives up to 14 days. If a shop
// owner changes a wholesaler's rate while that wholesaler already has lines
// in their cart, an older line keeps charging the OLD rate indefinitely,
// while anything added after the change gets the new one — the same cart
// silently mixing two prices for the same product with no indication why.
// The only existing backstop, VerifyOrderPricing, only runs AFTER the order
// is placed and HOLDS the mismatch for manual review: the customer has
// already paid by the time anyone notices.
//
// This runs in the background on the cart page and when the cart drawer
// opens — the places a customer is actually looking at these prices before
// checkout — see
// App\Http\Controllers\Proxy\CartRevalidateController and
// App\Services\LineRepricingService in hub-api-shopify. Corrected in place
// (rewritten via cart/change.js), never removed: the customer's whole
// configuration is worth more than a price that is a few percent off, and
// this is a UX fix for a confusing cart, not the actual pricing safety net —
// VerifyOrderPricing remains that.
//
// Self-contained on purpose — markup, scoped CSS and one vanilla IIFE, no
// build step and no JS dependency, the same shape as hs-reorder.liquid and
// esd-cart-update.js.
(function () {
    // Read while the script tag is still current: the customer id rides on it
    // (layout/theme.liquid) because the drawer, unlike the cart page, has no
    // markup of its own to carry it. `document.currentScript` is reliable
    // here (read synchronously at top-level script evaluation, which is true
    // for a deferred external script too) — the querySelector fallback is a
    // one-line hedge against a future edit moving this read somewhere it
    // would no longer hold, not a sign it is currently broken.
    var customerIdScript = (typeof document !== 'undefined'
        && (document.currentScript || document.querySelector('script[data-customer-id]'))) || null;
    var customerId = (customerIdScript && customerIdScript.getAttribute('data-customer-id')) || '';
    var inFlight = false;

    // The full chain — cart.js, a live Shopify company lookup and a live Expivi
    // price call per configured line — only runs when the shop's pricing
    // version (GET /apps/hub/pricing-version, one DB read server-side) differs
    // from the one this browser last revalidated at. A rate or price change
    // bumps that version; a version we could not learn about is bounded by
    // EXPIRY_MS, so a missed signal is wrong for a day at most. Every cart
    // view otherwise costs one tiny GET. VerifyOrderPricing remains the
    // actual backstop either way.
    var EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
    var POLL_MS = 60 * 1000; // at most one version GET a minute (drawer opens)
    var TIMEOUT_MS = 8000;
    var STORAGE_KEY = 'hs_cart_revalidated';
    var NOTICE_KEY = 'hs_cart_revalidated_notice';
    var lastPollAt = 0;

    /**
     * Pure so it is unit-testable without a real Date/localStorage.
     *
     * `stored` is `{version, checkedAt}` or null (never checked / storage
     * unavailable — err toward checking). A future `checkedAt` (clock change)
     * counts as expired, not as "wait until the clock catches up".
     */
    function needsRevalidate(stored, serverVersion, now, expiryMs) {
        if (!stored || stored.version == null || stored.checkedAt == null) return true;
        if (String(stored.version) !== String(serverVersion)) return true;
        return stored.checkedAt > now || (now - stored.checkedAt) >= expiryMs;
    }

    function cartRoot() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    /**
     * cart.js items this is worth asking about: only ones carrying
     * `_expivi_configuration` (Expivi V2) or `_cl_options` (LPO) — anything
     * else has no stored configuration to recompute a price FROM, and
     * repriceLines() would just pass it through unreported anyway. Filtering
     * here keeps the request small and keeps a plain retail product (no
     * configurator at all) out of it entirely.
     */
    function revalidationCandidates(cartItems) {
        return (cartItems || [])
            .filter(function (item) {
                var props = (item && item.properties) || {};
                return !!(props._expivi_configuration || props._cl_options);
            })
            .map(function (item) {
                return {
                    key: item.key,
                    variant_id: item.variant_id,
                    product_id: item.product_id,
                    properties: item.properties || {},
                };
            });
    }

    /**
     * Whether any candidate already carries a stored wholesale discount -
     * the one signal worth checking a GUEST's cart against. A guest's own
     * session never had a rate, so a discount already sitting on a line means
     * this cart was priced by somebody else (a signed-in wholesaler who has
     * since logged out, or the same browser on a shared computer) and is
     * stale for whoever is looking at it now. A genuinely retail guest cart -
     * the overwhelming majority of anonymous traffic - never matches this and
     * never reaches the revalidate call at all.
     */
    function hasStoredDiscount(candidates) {
        return (candidates || []).some(function (line) {
            return !!(line.properties && line.properties._unit_discount);
        });
    }

    /**
     * Never let a slow backend hang around: abort and treat as a failed
     * fetch. Wraps the whole call, not just the opts, so the abort timer is
     * always cleared once the request settles either way - an abort after
     * the request already resolved is a harmless no-op, but left running it
     * is one dangling timer per request for as long as TIMEOUT_MS takes to
     * fire. Every fetch in this file goes through this, including writeLine's
     * — without it, one `cart/change.js` call that hangs never settles the
     * reduce chain below, and `inFlight` stays true forever, silently
     * disabling every future check for the rest of the page's life.
     */
    function fetchWithTimeout(url, opts) {
        if (typeof AbortController === 'undefined') {
            return fetch(url, opts);
        }
        var ctl = new AbortController();
        var timer = setTimeout(function () { ctl.abort(); }, TIMEOUT_MS);
        opts.signal = ctl.signal;
        return fetch(url, opts).then(
            function (r) { clearTimeout(timer); return r; },
            function (e) { clearTimeout(timer); throw e; }
        );
    }

    function fetchVersion() {
        return fetchWithTimeout('/apps/hub/pricing-version', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (body) { return (body && body.success && body.version) ? String(body.version) : null; })
            .catch(function () { return null; });
    }

    function fetchCart() {
        return fetchWithTimeout(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
    }

    /**
     * `credentials: 'same-origin'`, not `omit`: the proxy is `public` mode so
     * an anonymous request still succeeds (empty `lines`), but a signed-in
     * customer needs their session cookie on the request for Shopify to
     * populate `logged_in_customer_id`, exactly as every other `/apps/hub/*`
     * call in this theme already does.
     */
    function revalidate(lines) {
        return fetchWithTimeout('/apps/hub/cart/revalidate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ lines: lines }),
        })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (body) { return body && body.success ? (body.lines || []) : null; })
            .catch(function () { return null; });
    }

    /**
     * `quantity` is re-read from the cart.js snapshot already in hand, not
     * carried through the server round trip: /cart/change.js's own docs only
     * confirm an omitted `quantity` defaults to 1 for the `selling_plan` case,
     * not the general one, and this endpoint has no other field to confirm a
     * multi-unit line either way. Sending the line's actual, just-fetched
     * quantity removes the guess entirely rather than relying on undocumented
     * behaviour - a multi-unit line silently dropping to 1 would be exactly
     * the kind of quiet data loss this whole feature exists to prevent.
     *
     * Pure so it is unit-testable without a real fetch.
     */
    function changeRequestBody(line, quantity) {
        var body = { id: line.key, properties: line.properties };
        if (quantity != null) {
            body.quantity = quantity;
        }
        return body;
    }

    function writeLine(line, quantity) {
        return fetchWithTimeout(cartRoot() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(changeRequestBody(line, quantity)),
        }).then(function (r) { return r.ok; }, function () { return false; });
    }

    /**
     * Styled as good news, not a warning: this fires only after a successful
     * correction, so an amber/warning-triangle treatment (the original
     * version of this) told the shopper something was wrong with THEIR cart,
     * when the fix was already applied for them. Uses the theme's own
     * success tokens (--green-bg/--green-bd/--green-text, the same family
     * `common.css` defines) rather than a one-off colour, so this reads as
     * part of the store, not a developer's debug banner - with plain hex
     * fallbacks for the unlikely case this runs somewhere common.css hasn't
     * loaded.
     */
    function showNotice(count) {
        // Dedupe rather than stack: a second trigger landing before the
        // first notice is dismissed (e.g. the drawer closed and reopened)
        // must not leave two banners on screen.
        var existing = document.querySelector('.cart-revalidate-notice');
        if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }

        var el = document.createElement('div');
        el.className = 'cart-revalidate-notice';
        el.setAttribute('role', 'status');

        var icon = document.createElement('span');
        icon.className = 'cart-revalidate-notice__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none">'
            + '<path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="1.75" '
            + 'stroke-linecap="round" stroke-linejoin="round"/></svg>';
        el.appendChild(icon);

        var text = document.createElement('span');
        text.textContent = count === 1
            ? 'One item’s price was updated to your current rate.'
            : count + ' items’ prices were updated to your current rate.';
        el.appendChild(text);

        if (!document.getElementById('cart-revalidate-style')) {
            var style = document.createElement('style');
            style.id = 'cart-revalidate-style';
            style.textContent = '.cart-revalidate-notice{display:flex;align-items:center;gap:10px;'
                + 'margin:0 0 20px;padding:12px 16px;'
                + 'background:var(--green-bg,#eaf3ee);border:1px solid var(--green-bd,#c7ddd0);'
                + 'border-radius:var(--r8,8px);font-family:var(--f-b,Inter,sans-serif);'
                + 'font-size:14px;line-height:1.4;color:var(--green-text,#2a5c3f);}'
                + '.cart-revalidate-notice__icon{display:inline-flex;flex:none;color:var(--green,#3d7a56);}';
            document.head.appendChild(style);
        }

        // Cart page grid, else the drawer's item list.
        var anchor = document.querySelector('.hs-cart-grid') || document.querySelector('.cart-drawer__items');
        (anchor && anchor.parentNode ? anchor.parentNode : document.body).insertBefore(el, anchor || null);
    }

    function quantityByKeyFrom(cartItems) {
        var map = {};
        (cartItems || []).forEach(function (item) { map[item.key] = item.quantity; });
        return map;
    }

    // Hard cap on how long the overlay is allowed to sit there - must never
    // trap a shopper behind a blur because one request hung. Longer than any
    // single fetchWithTimeout (8s) since the signed-in path can chain a few
    // of those, but short enough that "stuck" still reads as "stuck", not as
    // a normal wait.
    var OVERLAY_TIMEOUT_MS = 10000;
    var overlayTimer = null;

    // Re-armed before every serial write below, not just once on show: a
    // multi-line wholesale cart chains several writeLine calls, and a single
    // fixed 10s budget for the whole chain would make the overlay vanish
    // mid-correction on a slow connection while writes were still landing.
    function rearmOverlayTimer() {
        clearTimeout(overlayTimer);
        overlayTimer = setTimeout(hideOverlay, OVERLAY_TIMEOUT_MS);
    }

    /**
     * Full-viewport blur, shown only once a check has already confirmed real
     * work is needed (a version mismatch / 24h expiry / a guest's stray
     * discount) - never on the common "nothing to check" pass, which never
     * calls this at all. Deliberately covers the WHOLE page, not just the
     * checkout button: a scoped overlay could not reach Shopify's own
     * accelerated checkout buttons (Shop Pay, PayPal - injected via
     * `content_for_additional_checkout_buttons`, outside this theme's
     * control), but a page-level one sits above everything regardless of
     * what drew it.
     */
    function showOverlay() {
        if (document.getElementById('cart-revalidate-overlay')) return;

        if (!document.getElementById('cart-revalidate-overlay-style')) {
            var style = document.createElement('style');
            style.id = 'cart-revalidate-overlay-style';
            style.textContent = '.cart-revalidate-overlay{position:fixed;inset:0;z-index:2147483647;'
                + 'display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px;'
                + 'background:rgba(255,255,255,.6);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px);}'
                + '.cart-revalidate-overlay__spinner{width:32px;height:32px;border-radius:50%;'
                + 'border:3px solid rgba(0,0,0,.15);border-top-color:#111;animation:cart-revalidate-spin .8s linear infinite;}'
                + '.cart-revalidate-overlay__text{font-size:14px;color:#111;}'
                + '@keyframes cart-revalidate-spin{to{transform:rotate(360deg);}}';
            document.head.appendChild(style);
        }

        var el = document.createElement('div');
        el.className = 'cart-revalidate-overlay';
        el.id = 'cart-revalidate-overlay';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');

        var spinner = document.createElement('div');
        spinner.className = 'cart-revalidate-overlay__spinner';
        spinner.setAttribute('aria-hidden', 'true');
        el.appendChild(spinner);

        var text = document.createElement('div');
        text.className = 'cart-revalidate-overlay__text';
        text.textContent = 'Checking your cart’s price…';
        el.appendChild(text);

        document.body.appendChild(el);

        rearmOverlayTimer();
    }

    function hideOverlay() {
        clearTimeout(overlayTimer);
        var el = document.getElementById('cart-revalidate-overlay');
        if (el && el.parentNode) {
            el.parentNode.removeChild(el);
        }
    }

    /**
     * Sends `candidates` to the server, writes back whatever it says changed,
     * and reloads/notifies on a successful write. Shared by both the
     * signed-in and guest paths below — the actual network+write mechanics
     * don't differ between "why we decided to check", only the gating does.
     *
     * Resolves `true` when there is nothing left pending (nothing needed
     * changing, or every write landed) - the caller's signal that it is safe
     * to record this check as done; `false` means retry on the next trigger.
     *
     * Only ever called once a cheap check has already confirmed this is a
     * real, non-speculative revalidation - so showing the overlay here, not
     * any earlier, means a shopper whose cart needs nothing sees nothing at
     * all, ever.
     */
    function revalidateAndWrite(candidates, quantityByKey) {
        showOverlay();

        return revalidate(candidates).then(function (changed) {
            if (!changed) { hideOverlay(); return false; } // request failed - retry next trigger
            if (!changed.length) { hideOverlay(); return true; } // nothing needed changing

            // Serially, not Promise.all: cart/change.js mutates one shared
            // cart object, and concurrent writes drop each other's changes —
            // same reasoning as esd-cart-update.js's write loop.
            var written = 0;
            return changed.reduce(function (chain, line) {
                return chain.then(function () {
                    var quantity = quantityByKey[line.key];
                    // Every line in `changed` came from this same snapshot,
                    // so a miss here "should not happen" - but a server
                    // echoing back a key the snapshot never had, or the cart
                    // mutating mid-check, are both real possibilities.
                    // Sending the write anyway with quantity omitted would
                    // silently reintroduce the exact drop-to-1 bug this file
                    // exists to fix, and `r.ok` would still count it as
                    // written. Skipping it instead means `written` stays
                    // below `changed.length`, so the caller withholds its
                    // "done" mark and the next trigger retries against a
                    // fresh cart.js read.
                    if (quantity == null) {
                        return;
                    }
                    rearmOverlayTimer();
                    return writeLine(line, quantity).then(function (ok) { if (ok) written++; });
                });
            }, Promise.resolve()).then(function () {
                if (written > 0) {
                    if (document.querySelector('.hs-cart-grid')) {
                        // The cart page was rendered with the old prices;
                        // reload so lines and totals match what checkout
                        // will charge. Notice shown after the reload. Overlay
                        // deliberately left showing - it dies with the page
                        // the instant the reload actually happens, and
                        // hiding it a moment early would flash the stale
                        // content underneath for one frame first.
                        try { window.sessionStorage.setItem(NOTICE_KEY, String(written)); } catch (e) {}
                        window.location.reload();
                    } else {
                        // Drawer: let the theme re-render it, then say so.
                        hideOverlay();
                        document.addEventListener('cart-drawer:refreshed', function () {
                            showNotice(written);
                        }, { once: true });
                        document.dispatchEvent(new CustomEvent('cart:refresh'));
                    }
                } else {
                    hideOverlay();
                }

                return written === changed.length;
            });
        });
    }

    /**
     * Signed-in path: version-gated, same as always - only runs the full
     * chain when the shop's pricing stamp has moved since this browser last
     * checked (or EXPIRY_MS has passed without hearing).
     */
    function checkSignedIn() {
        var storageKey = STORAGE_KEY + ':' + customerId;

        function markChecked(version) {
            try {
                window.localStorage.setItem(storageKey, JSON.stringify({ version: version, checkedAt: Date.now() }));
            } catch (e) {}
        }

        return fetchVersion().then(function (version) {
            // Could not learn the version (offline, timeout): do nothing.
            if (!version) return;

            var stored = null;
            try {
                stored = JSON.parse(window.localStorage.getItem(storageKey));
            } catch (e) {
                // Private browsing / storage disabled / corrupt — fall through
                // and check; erring toward an extra check beats never checking.
            }
            if (!needsRevalidate(stored, version, Date.now(), EXPIRY_MS)) return;

            return fetchCart().then(function (cart) {
                if (!cart) return;
                var candidates = revalidationCandidates(cart.items);
                if (!candidates.length) return markChecked(version);

                return revalidateAndWrite(candidates, quantityByKeyFrom(cart.items)).then(function (settled) {
                    if (settled) markChecked(version);
                });
            });
        });
    }

    /**
     * Guest path: no version concept (there is no per-customer anything to
     * compare against), so instead of a stamp this checks the one signal
     * that is actually worth asking about - does this cart already carry a
     * discount a guest should never have. No persisted throttle either: the
     * condition is self-clearing, since a successful correction removes the
     * very thing this looks for, so the next trigger finds nothing and no-ops
     * for free without needing a stored flag to tell it that.
     *
     * Costs one cart.js fetch per throttled trigger for every guest, where
     * the old blanket `if (!customerId) return;` cost guests nothing at all -
     * a deliberate tradeoff: POLL_MS already caps it to once a minute, and
     * it's the only way to see whether a guest's cart carries a stale
     * discount at all.
     */
    function checkGuest() {
        return fetchCart().then(function (cart) {
            if (!cart) return;
            var candidates = revalidationCandidates(cart.items);
            if (!hasStoredDiscount(candidates)) return;

            return revalidateAndWrite(candidates, quantityByKeyFrom(cart.items));
        });
    }

    /**
     * One background check. Silent on every failure: this is a UX aid and
     * must never block, delay or redirect a shopper heading to checkout —
     * VerifyOrderPricing is the real safety net.
     */
    function check() {
        if (inFlight) return;
        if (Date.now() - lastPollAt < POLL_MS) return;
        lastPollAt = Date.now();
        inFlight = true;

        // Promise.resolve(...), not the bare return value: every current
        // branch of checkSignedIn()/checkGuest() does return a promise, but a
        // future branch returning undefined would otherwise throw calling
        // .catch() on it synchronously, leaving inFlight stuck true forever.
        var run = Promise.resolve(customerId ? checkSignedIn() : checkGuest());
        run.catch(function () {}).then(function () { inFlight = false; });
    }

    // The version/expiry gate is per customer, but the cart is per browser: a
    // different user (or a guest) may have filled it since this customer last
    // checked, and their lines carry THEIR prices. Whenever the person on this
    // browser changes, forget the gate so the next cart view revalidates fully.
    var OWNER_KEY = 'hs_cart_owner';
    function resetGateOnCustomerChange() {
        try {
            var prev = window.localStorage.getItem(OWNER_KEY);
            if (prev === customerId) return;
            window.localStorage.setItem(OWNER_KEY, customerId);
            if (customerId) window.localStorage.removeItem(STORAGE_KEY + ':' + customerId);
        } catch (e) {}
    }

    function init() {
        resetGateOnCustomerChange();
        // Shown after the reload above, so it survives the page re-render.
        try {
            var pending = Number(window.sessionStorage.getItem(NOTICE_KEY));
            window.sessionStorage.removeItem(NOTICE_KEY);
            if (pending > 0 && document.querySelector('.hs-cart-grid')) showNotice(pending);
        } catch (e) {}

        // Triggers: the cart page itself and the drawer opening. The drawer's
        // `dialog:after-show` does not bubble, so listen on the element.
        if (document.querySelector('.hs-cart-grid')) check();

        var drawer = document.getElementById('cart-drawer');
        if (drawer) drawer.addEventListener('dialog:after-show', check);
    }

    // Exported for the node test harness. Guarded so this file can be
    // `require()`d outside a browser (see expivi-viewer.js for the same
    // shape) without the DOM-touching code below ever running.
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            revalidationCandidates: revalidationCandidates,
            needsRevalidate: needsRevalidate,
            changeRequestBody: changeRequestBody,
            hasStoredDiscount: hasStoredDiscount,
        };
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
